import {
  createMultiStyleConfigHelpers,
  createStylesContext,
  StyleThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import {
  CreateCustomStyleReturn,
  MultiStyleType,
} from '@chakra-ui/styled-system'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { Radii, Size } from '~/types/ThemeTypes'
import { getName } from '~/utils/objectUtils'

import { phoneColorKeys } from './_colors'
import { PhoneComponentSizes } from './Constants'

const {
  Keypad: { Button: KeypadButtonColors },
  CallButton: CallButtonColors,
} = phoneColorKeys.Phone

const { FontColor } = themeColorSectionKeys.defaults

const {
  Keypad: {
    ButtonContainer: { boxSize: ButtonContainerBoxSize },
    Button: { boxSize: ButtonBoxSize },
  },
} = PhoneComponentSizes

const keypadParts = [
  'keypad',
  'buttonContainer',
  'button',
  'callButton',
  'primaryCharacter',
  'secondaryCharacters',
] as const

const helpers = createMultiStyleConfigHelpers(keypadParts)

export const KeypadStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    keypad: {
      justifyContent: 'center',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      boxSize: Size.full,
      borderRadius: Radii.full,
    },
    button: {
      borderRadius: Radii.full,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      bg: KeypadButtonColors.Background,
      color: FontColor.Default,
      _hover: {
        bg: KeypadButtonColors.Hover.Background,
        '& > p': {
          color: KeypadButtonColors.Hover.FontColor,
        },
      },
      _active: {
        bg: KeypadButtonColors.Active.Background,
        '& > p': {
          color: KeypadButtonColors.Active.FontColor,
        },
      },
    },
    callButton: {
      borderRadius: Radii.full,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      _active: {
        bg: CallButtonColors.Variants.Start.Active.Background,
      },
    },
    primaryCharacter: {
      fontWeight: '700',
      textAlign: 'center',
      userSelect: 'none',
    },
    secondaryCharacters: {
      fontSize: '10px',
      fontWeight: '700',
      textAlign: 'center',
      userSelect: 'none',
    },
  },
  sizes: {
    [Size.sm]: {
      keypad: {
        gap: '16px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.sm.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.sm.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.sm.px,
        _active: {
          boxSize: ButtonContainerBoxSize.sm.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.sm.px,
        _active: {
          boxSize: ButtonContainerBoxSize.sm.px,
        },
      },
      primaryCharacter: {
        fontSize: '32px',
        lineHeight: '32px',
        marginBottom: '-4px',
      },
    },
    [Size.md]: {
      keypad: {
        gap: '24px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.md.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.md.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.md.px,
        _active: {
          boxSize: ButtonContainerBoxSize.md.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.md.px,
        _active: {
          boxSize: ButtonContainerBoxSize.md.px,
        },
      },
      primaryCharacter: {
        fontSize: '40px',
        lineHeight: '40px',
        marginBottom: '-4px',
      },
    },
    [Size.lg]: {
      keypad: {
        gap: '28px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.lg.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.lg.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.lg.px,
        _active: {
          boxSize: ButtonContainerBoxSize.lg.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.lg.px,
        _active: {
          boxSize: ButtonContainerBoxSize.lg.px,
        },
      },
      primaryCharacter: {
        fontSize: '40px',
        lineHeight: '40px',
        marginBottom: '-4px',
      },
    },
  },
  defaultProps: {
    size: Size.md,
  },
})

const keypadKeyName = getName({ KeypadStyle })

export const useKeypadStyles = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(keypadKeyName, props) as MultiStyleType<
    typeof keypadParts
  >
}

export const createKeypadStylesContext = () => {
  return createStylesContext(keypadKeyName) as CreateCustomStyleReturn<
    typeof keypadParts
  >
}
